import { useEffect, useState } from "react"

import { RESOURCES_NAMES } from "@/const/resource.constants"
import { CHECKBOX_ISSUE_DATA_ID } from "@/const/table-constants"
import { isDataAvailable } from "@/helpers/context.helper"
import { checkboxOnClick } from "@/helpers/table.helpers"
import { predicateHelpers } from "@/hooks/useHttp"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { useGetIssuesForResource } from "@/organisms/Drawers/applications/inventory/useInventory"
import { IssuesFilter } from "@/organisms/Drawers/components/IssuesFilter"
import { TabDrawerTemplate } from "@/organisms/Drawers/components/TabDrawerTemplate"
import { TRIAGE_DRAWER_ID } from "@/organisms/Drawers/const"
import { useCounts } from "@/organisms/Drawers/hooks/useCounts"
import { useDrawer } from "@/organisms/Drawers/hooks/useDrawer"
import { useParamsFilter } from "@/organisms/Drawers/hooks/useParamsFilter"
import { LabelModal } from "@/organisms/LabelModal"
import {
  actions,
  useSelection
} from "@/pages/TriagePage/application/useSelection"
import { IssueActions } from "@/pages/TriagePage/components/molecules/IssueActions"
import { getColumnsTable } from "./getColumnsTable"

import type { Issue } from "@/domain/issue"
import type { IssuesTabProps } from "./IssuesTab.types"

export const Tab = (props: IssuesTabProps) => {
  const tabKey = props.tabKey || "scaIssues"
  const { getInitialParamsFilter, record, headerComponent, exportQueryParams } =
    props
  const { defaultParams, ...restProps } = props
  const filtersContext = useParamsFilter({
    filterKey: tabKey,
    initialParams: getInitialParamsFilter(),
    ...restProps
  })

  const { t } = useTranslation()

  const resourceId = record?.id
  const [dataContext, countsContext] = useGetIssuesForResource(resourceId, {
    ...defaultParams,
    ...filtersContext.params
  })
  const [selectionState, dispatch] = useSelection()
  const { selectedAll, updating } = selectionState

  useCounts(tabKey, countsContext, props)

  useEffect(() => {
    if (dataContext.isLoading) return
    if (dataContext.isRefetching) return
    if (!dataContext.response) return
    if (!resourceId) return

    const { data, metadata } = dataContext.response
    const issuesByProject = data.reduce(
      (acc: Record<string, any[]>, item: any) => {
        acc[resourceId].push(item)

        return acc
      },
      { [resourceId]: [] } as Record<string, any[]>
    )

    Object.keys(issuesByProject).forEach((projectId: string) => {
      dispatch(
        actions.setIdsNestedTable(issuesByProject[projectId], projectId, {
          issuesTotal: metadata?.total
        })
      )
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataContext.isLoading, dataContext.isRefetching, props.isActive])

  const [issueRecord, setIssueRecord] = useState<null | Issue>(null)
  const { onRowClick } = useDrawer(TRIAGE_DRAWER_ID)
  const { logger } = useLogger()
  const componentName = `tab:${tabKey || ""}_drawer:${props.drawerId || ""}`
  const { data, metadata } = dataContext?.response || {}

  return (
    <>
      <TabDrawerTemplate
        isLoading={updating}
        dataContext={dataContext}
        filtersContext={filtersContext}
        getColumnsTable={getColumnsTable}
        expandable={null}
        onRow={(record) => ({
          onClick: (event: React.MouseEvent<HTMLElement>) =>
            checkboxOnClick(event, {
              record: record as { key: string },
              logger,
              cb: onRowClick,
              querySelector: `[${CHECKBOX_ISSUE_DATA_ID}]`,
              analyticEvent: { name: props.drawerId, placement: tabKey }
            })
        })}
        rowSelection={{
          type: "checkbox",
          getCheckboxProps: (record) =>
            ({ [CHECKBOX_ISSUE_DATA_ID]: record.id }) as any,
          selectedRowKeys: selectedAll
            ? (data || []).map(({ key }: { key: string }) => key)
            : selectionState.selectedNestedIds,
          onSelect: (record, selected) => {
            if (resourceId)
              dispatch(actions.onSelectRow(resourceId, record.id, selected))
          },
          onChange: (_, __, { type }) => {
            if (type === "all") dispatch(actions.selectAll())
          }
        }}
        FilterComponent={
          <IssuesFilter
            t={t}
            searchedVulnerabilities={metadata?.searchedVulnerabilities}
            includeApplicationFilter={props.includeApplicationFilter}
            exportQueryParams={exportQueryParams}
            isDataAvailable={isDataAvailable(dataContext)}
            defaultParams={defaultParams}
            placement={componentName}
            {...filtersContext}
          />
        }
        asideFooter={
          <IssueActions
            showLoadingMessage
            resourceId={resourceId}
            params={{ ...filtersContext.params, ...defaultParams }}
            getOptionsUseDismissIssues={() => (queryClient) => ({
              onSuccess: () => {
                queryClient.removeQueries({
                  predicate: predicateHelpers.includesKeysPredicate([
                    RESOURCES_NAMES.TRIAGE.PROJECTS
                  ])
                })
                return dataContext?.refetch?.()
              },
              onSettled: undefined
            })}
            componentName={componentName}
            totalIssuesCount={metadata?.total}
          />
        }
        headerComponent={headerComponent}
        setIssueRecord={setIssueRecord}
      />
      {issueRecord && (
        <LabelModal
          placement="tooltip"
          component="triage-tab"
          record={issueRecord}
          onClose={() => setIssueRecord(null)}
        />
      )}
    </>
  )
}
